import { FC, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Container, Typography } from '@mui/material';
import classNames from 'classnames';

import CardWithShadow from '@components/CardWithShadow/CardWithShadow';
import EmptyCard from '@components/EmptyCard/EmptyCard';
import HtmlContent from '@components/HtmlContent/HtmlContent';
import Loadable from '@components/Loadable/Loadable';
import SearchSelectBox from '@components/SearchSelectBox/SearchSelectBox';
import { IMedicationsItem } from '@components/SearchSelectBox/SearchSelectBox.interface';
import urls from '@routes/urls';
import { dashboardOrgDataSelector } from '@store/features/dashboardOrgInfo/selectors';
import { handleGetMedicationDetails } from '@store/features/medications/medicationDetails/actions';
import {
  medicationDetailsActiveIdSelector,
  medicationDetailsErrorSelector,
  medicationDetailsIsLoadingSelector,
  medicationDetailsSelector,
} from '@store/features/medications/medicationDetails/selectors';
import { removeGetMedicationDetails } from '@store/features/medications/medicationDetails/slice';
import { handleGetMedicationSearch } from '@store/features/medications/medicationSearch/actions';
import {
  medicationSearchIsLoadingSelector,
  medicationSearchPaginationSelector,
  medicationSearchTableDataSelector,
} from '@store/features/medications/medicationSearch/selectors';
import { removeMedicationsSearch } from '@store/features/medications/medicationSearch/slice';
import useDispatch from '@store/hooks/useDispatch';
import useDrugClasses from '@store/hooks/useDrugClasses';
import useMedicationSettings from '@store/hooks/useMedicationSettings';
import handleGetBannedMedications from '@utils/handleGetBannedMedications';

import AdditionalInformationCard from './AdditionalInformationCard/AdditionalInformationCard';
import InOutGrid from './InOutGrid/MedicationInOutGrid';
import MedicationSearchResultCard from './MedicationSearchResultCard/MedicationSearchResultCard';
import SubmitAnInquiryCTACard from './SubmitAnInquiryCTACard/SubmitAnInquiryCTACard';
import SupplementsNotificationsCard from './SupplementsNotificationsCard/SupplementsNotificationsCard';

import './style.scss';

const MedicationsPage: FC = (): JSX.Element => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const drugClasses = useDrugClasses();
  const medicationSettings = useMedicationSettings();
  const medicationSearch = useSelector(medicationSearchTableDataSelector);
  const medicationSearchPagination = useSelector(medicationSearchPaginationSelector);
  const medicationLoading = useSelector(medicationSearchIsLoadingSelector);
  const medicationDetails = useSelector(medicationDetailsSelector);
  const medicationDetailsActiveId = useSelector(medicationDetailsActiveIdSelector);
  const medicationDetailsIsLoading = useSelector(medicationDetailsIsLoadingSelector);
  const medicationDetailsError = useSelector(medicationDetailsErrorSelector);
  const dashboardOrgInfo = useSelector(dashboardOrgDataSelector);
  const canUseSupplementInquiry = dashboardOrgInfo?.canUseSupplementInquiry;
  const medicationNotFoundText = dashboardOrgInfo?.medicationNotFoundText;
  const hasInOutCompBan = dashboardOrgInfo?.hasInOutCompBan;

  useEffect(() => {
    !medicationSearch.length && dispatch(removeGetMedicationDetails());
  }, [medicationSearch, dispatch]);

  const [searchValue, setSearchValue] = useState<string>('');

  const [selectedMedication, setSelectedMedication] = useState<IMedicationsItem | null>(null);

  const handleSelectMedication = (event: SyntheticEvent, value: IMedicationsItem | null): void => {
    setSelectedMedication(value);
    value ? dispatch(handleGetMedicationDetails(value.id)) : dispatch(removeMedicationsSearch());
  };

  const handleSearchMedication = (value: string): void => {
    dispatch(removeMedicationsSearch());
    setSearchValue(value.trim());
  };

  const searchMedication = useCallback(
    async (value: string): Promise<void> => {
      const page = medicationSearchPagination?.page || 1;
      const pageCount = medicationSearchPagination?.pageCount || 0;

      if ((page === pageCount && pageCount > 1) || value.length < 3) {
        return;
      }

      dispatch(
        handleGetMedicationSearch({
          search: value,
          page: page < pageCount ? page + 1 : page,
        }),
      );
    },
    [dispatch, medicationSearchPagination],
  );

  useEffect(() => {
    searchMedication(searchValue);
  }, [searchValue]);

  const handleRetryFetchSelectedMedicationDetails = useCallback(() => {
    medicationDetailsActiveId && dispatch(handleGetMedicationDetails(medicationDetailsActiveId));
  }, [dispatch, medicationDetailsActiveId]);

  const administrations = useMemo(
    () =>
      medicationDetails?.rules
        ?.flatMap(rule =>
          rule.administrations.map(administration => ({
            ...administration,
            allowedIn: rule.allowedIn,
            allowedOutside: rule.allowedOutside,
            condition: rule.condition,
          })),
        )
        .sort((a, b) => (a.condition ? 1 : -1) - (b.condition ? 1 : -1))
        .filter(({ name }) => name !== 'NOTE'),
    [medicationDetails],
  );

  const rules = useMemo(() => medicationDetails?.rules, [medicationDetails]);

  const conditions = useMemo(() => {
    if (medicationDetails?.rules && medicationDetails?.rules?.length > 0) {
      return medicationDetails.rules.reduce((acc, rule) => {
        if (rule.condition && !acc.includes(rule.condition)) {
          acc.push(`${rule.condition}\n`);
        }
        return acc;
      }, [] as string[]);
    }
    return [];
  }, [medicationDetails]);

  useEffect(
    () => () => {
      dispatch(removeMedicationsSearch());
      dispatch(removeGetMedicationDetails());
      setSelectedMedication(null);
    },
    [dispatch],
  );

  return (
    <Container maxWidth="lg" className="page medications-page">
      <Typography variant="h1" className="medications-page__title">
        {t('medicationLookup.title')}
      </Typography>

      <Typography variant="body1" className="medications-page__description">
        {t('medicationLookup.description')}
      </Typography>

      <Typography variant="body1" className="medications-page__search-tip">
        {t('medicationLookup.searchTip')}
      </Typography>

      <Box className="medications-page__control-part">
        <Box className="medications-page__main">
          <SearchSelectBox
            className="medications-page__select"
            title={t('medicationLookup.medicationName')}
            items={medicationSearch}
            selected={selectedMedication}
            onSelect={handleSelectMedication}
            onSearch={handleSearchMedication}
            onLoadMore={searchMedication}
            isLoading={medicationLoading}
            searchValue={searchValue}
            noOptionsText={
              medicationNotFoundText && <HtmlContent>{medicationNotFoundText}</HtmlContent>
            }
            hasInOutCompBan={hasInOutCompBan}
          />
          {hasInOutCompBan && administrations && (
            <InOutGrid rules={rules} administrations={administrations} />
          )}
        </Box>

        <Box
          className={classNames('medications-page__right-aside', {
            'medications-page__right-aside_half-width': hasInOutCompBan,
          })}
        >
          <CardWithShadow className="medications-page__search-result">
            <Loadable
              isLoading={medicationDetailsIsLoading}
              error={medicationDetailsError && t('errors.medicationDetailsError')}
              onRetry={handleRetryFetchSelectedMedicationDetails}
            >
              {hasInOutCompBan ? (
                <AdditionalInformationCard
                  synonyms={medicationDetails?.synonyms}
                  drugClassIds={handleGetBannedMedications(
                    medicationDetails?.drugClassIds || [],
                    drugClasses,
                  )}
                  medicationDetails={medicationDetails}
                  conditions={conditions}
                />
              ) : selectedMedication ? (
                <MedicationSearchResultCard
                  prescription={selectedMedication.name}
                  isBanned={!selectedMedication.isAllowed}
                  bannedText={medicationSettings?.bannedText || ''}
                  notBannedText={medicationSettings?.notBannedText || ''}
                  isShowMedicationExceptionLink={!!medicationSettings?.showMedicationExceptionLink}
                  bannedDrugClasses={handleGetBannedMedications(
                    selectedMedication.drugClassIds || [],
                    drugClasses,
                  )}
                  linkHref={medicationSettings?.medicationExceptionFileUrl}
                  linkTitle={medicationSettings?.medicationExceptionFileName}
                />
              ) : (
                <EmptyCard
                  icon="/images/img-medicationbottle.svg"
                  description={t('medicationLookup.emptyResultDescription')}
                  borderType="none"
                />
              )}
            </Loadable>
          </CardWithShadow>

          {!!canUseSupplementInquiry && (
            <>
              <Box className="medications-page__submit-an-inquiry-cta-card">
                <SubmitAnInquiryCTACard
                  link={{
                    href: urls.inquiryDraft,
                  }}
                />
              </Box>

              <Box className="medications-page__supplements-notifications-card">
                <SupplementsNotificationsCard
                  icon="/images/img-dietarysupplement.svg"
                  link={{
                    href: urls.inquiryDraft,
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default MedicationsPage;
